import type { WidgetEffectParams } from '@swf/core/store';
import type { BasicSportsWidgetConfig } from '@swf/sports/pages/types';
import { sportsUrlParamChange, sportsWidgetPollingBehavior } from '@swf/sports/utils/actions';
import buildSportsRequest from '@swf/sports/utils/buildSportsRequest';
import { getWidgetCancellationSignal } from '@swf/sports/utils/getWidgetCancellationSignal';
import { hasRequestAborted } from '@swf/sports/utils/hasRequestAborted';
import { isMeetingBasedEventTypeId } from '@swf/sports/utils/isEventTypeId';

import type { EventTypeInfoSuccessResponse } from '../types';

import type { WidgetDispatch, WidgetSelect } from './types';

export const effects = {
  async preload(_payload: undefined, params: WidgetEffectParams<BasicSportsWidgetConfig>) {
    const { dispatch, widget, select, rootState } = params;

    const widgetSelect: WidgetSelect = select[widget.uid];
    const widgetDispatch: WidgetDispatch = dispatch[widget.uid];

    const { eventTypeInfoRequested, clear }: WidgetDispatch = widgetDispatch;

    sportsUrlParamChange(params, 'eventTypeId')?.subscribe(widgetDispatch.eventTypeInfoRequested);
    sportsWidgetPollingBehavior(params).subscribe({
      next: (pollingSource) =>
        (pollingSource === 'AUTH_CHANGE' || pollingSource === 'INTERVAL') && widgetDispatch.poll(),
      complete: () => clear()
    });
    if (!widgetSelect.hasPreloaded(rootState)) {
      await eventTypeInfoRequested();
    }
  },

  async poll(payload: undefined, params: WidgetEffectParams<BasicSportsWidgetConfig>) {
    const { eventTypeId } = params.select.sports.getUrlParams(params.rootState);
    if (isMeetingBasedEventTypeId(Number(eventTypeId))) {
      return;
    }

    await effects.eventTypeInfoRequested(payload, params);
  },

  async eventTypeInfoRequested(
    _payload: undefined,
    params: WidgetEffectParams<BasicSportsWidgetConfig>
  ): Promise<void> {
    const { dispatch, select, rootState, request, widget } = params;
    const { eventTypeId } = select.sports.getUrlParams(rootState);
    const alias = select.cms.getPage(rootState)?.alias;
    const widgetDispatch: WidgetDispatch = dispatch[widget.uid];
    const sportsRequest = buildSportsRequest(request, rootState);

    if (!alias) return;

    try {
      const {
        body: { data }
      } = await sportsRequest<EventTypeInfoSuccessResponse>({
        path: `/event-type-info/${eventTypeId}`,
        signal: getWidgetCancellationSignal(params)
      });

      widgetDispatch.dataLoadSuccess(data);
    } catch (error) {
      if (hasRequestAborted(error)) return;
      widgetDispatch.dataLoadFailed();
    }
  }
};
